import { makeid } from 'utils/helpers/makeid';

import ScriptWrapper from './script-wrapper';
import { addField, formatList, hasField, mapValuesToText } from '../helper';

import type { BlurbContext, ITemplateProps } from '../types';
import type {
  ExtractStepValues,
  TFormTemplateItem,
} from '../../../molecules/base-form-template/types';

export const createAssist991Template = (data: ITemplateProps) => {
  const { userFullname } = data;
  return [
    {
      key: makeid(10),
      name: 'greetingComplianceHipaaVerification',
      type: 'checkbox',
      title: <p>Greeting & Compliance / HIPAA Verification</p>,
      description: (
        <ScriptWrapper>
          "Hello, this is {userFullname} with Tellihealth. For quality and
          compliance purposes, this call may be recorded. To make sure I’m
          speaking with the right person, can you please confirm your full name
          and date of birth?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient identity verified (name + DOB)',
          value: 'patientIdentityVerified',
        },
        {
          key: makeid(10),
          label: 'Caregiver present → Consent confirmed',
          value: 'caregiverPresentConsentConfirmed',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "Are you the patient’s caregiver? Do I have the patient’s
                  consent to speak with you today?"
                </ScriptWrapper>
              ),
            },
          ],
        },
        {
          key: makeid(10),
          label: 'Recorded line notification provided',
          value: 'recordedLineNotificationProvided',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'emergencySituationReported',
      type: 'checkbox',
      title: <p>Acknowledgement of Emergency Situation</p>,
      description: (
        <ScriptWrapper>
          "Thank you for telling me what’s going on. I’m really glad you reached
          out. My priority right now is helping keep you safe."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Acute medical symptoms reported',
          value: 'acuteMedicalSymptomsReported',
        },
        {
          key: makeid(10),
          label: 'Sudden change in condition',
          value: 'suddenChangeInCondition',
        },
        {
          key: makeid(10),
          label: 'Injury or accident',
          value: 'injuryOrAccident',
        },
        {
          key: makeid(10),
          label: 'Other emergency concern',
          value: 'other',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'textarea',
              name: 'emergencySituationReportedOther',
            },
          ],
        },
      ],
    },
    {
      key: makeid(10),
      name: 'emergencyStatusConfirmed',
      type: 'radio',
      title: <p>Clarify Emergency Status (Response-Based)</p>,
      description: (
        <ScriptWrapper>
          "Based on what you’ve described, this sounds like an emergency. Are
          you experiencing this right now?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient experiencing symptoms at time of call',
          value: 'patientExperiencingSymptomsAtTimeOfCall',
        },
        {
          key: makeid(10),
          label: 'Emergency concern ongoing',
          value: 'emergencyConcernOngoing',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'patientLocationConfirmed',
      type: 'radio',
      title: <p>Confirm Patient Location (Required)</p>,
      description: (
        <ScriptWrapper>
          "To get you help, I need to know exactly where you are. Can you tell
          me your current location, including the address if you know it?"
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Location confirmed',
          value: 'locationConfirmed',
        },
        {
          key: makeid(10),
          label: 'Partial location obtained',
          value: 'partialLocationObtained',
        },
        {
          key: makeid(10),
          label: 'Unable to obtain location',
          value: 'unableToObtainLocation',
          hideSubitemsIfParentNotSelected: true,
          subItems: [
            {
              key: makeid(10),
              type: 'empty',
              name: '_empty',
              description: (
                <ScriptWrapper>
                  "That's okay — are you at home, with someone, or somewhere
                  else?"
                </ScriptWrapper>
              ),
            },
          ],
        },
      ],
      items: [
        {
          key: makeid(10),
          name: 'patientLocation',
          type: 'textarea',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'patientKeptOnLine',
      type: 'radio',
      title: <p>Keep Patient Engaged & On the Line</p>,
      description: (
        <ScriptWrapper>
          "Thank you for staying on the phone with me. I’m here with you. We’re
          going to focus on getting help to you as quickly as possible."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Patient remained on line',
          value: 'patientRemainedOnLine',
        },
        {
          key: makeid(10),
          label: 'Intermittently responsive',
          value: 'intermittentlyResponsive',
        },
        {
          key: makeid(10),
          label: 'Call disconnected unexpectedly',
          value: 'callDisconnectedUnexpectedly',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'emergencyResponseInitiated',
      type: 'checkbox',
      title: <p>When to Tell the Patient Help Is Being Sent</p>,
      description: (
        <ScriptWrapper>
          "Because of what you’re experiencing, I’m concerned about your safety.
          To help keep you safe, I’m going to have emergency services come to
          your location. If you can, please stay on the phone with me while help
          is on the way."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Supervisor notified',
          value: 'supervisorNotified',
        },
        {
          key: makeid(10),
          label: 'Teammate contacted 911',
          value: 'teammateContacted911',
        },
        {
          key: makeid(10),
          label: 'Emergency services dispatched',
          value: 'emergencyServicesDispatched',
        },
      ],
    },
    {
      key: makeid(10),
      name: 'safetySupportProvided',
      type: 'checkbox',
      title: <p>Safety Support While Waiting</p>,
      description: (
        <ScriptWrapper>
          "If you’re able, try to stay in a safe position. If someone is nearby,
          can you let them know you need help? If your symptoms change or
          worsen, please tell me right away."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Encouraged safe positioning',
          value: 'encouragedSafePositioning',
        },
        {
          key: makeid(10),
          label: 'Encouraged support person presence',
          value: 'encouragedSupportPersonPresence',
        },
        {
          key: makeid(10),
          label: 'Monitored symptom changes',
          value: 'monitoredSymptomChanges',
        },
        {
          key: makeid(10),
          label: 'Not appropriate / not possible',
          value: 'notAppropriateNotPossible',
        },
      ],
    },
    {
      key: makeid(10),
      name: '_empty',
      type: 'empty',
      title: <p>Background Escalation (NOT READ ALOUD)</p>,
      description: (
        <ScriptWrapper>
          <ul>
            <li>• Alert supervisor or designated emergency lead </li>
            <li>
              • Teammate contacts 911 with patient location and situation
              details
            </li>
            <li>
              • MA remains on the line with patient at all times if possible
            </li>
            <li>• Supervisor provides live guidance</li>
          </ul>
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
    },
    {
      key: makeid(10),
      type: 'empty',
      name: '_empty',
      title: <p>Supportive Presence Until Help Arrives</p>,
      description: (
        <ScriptWrapper>
          "I’m still here with you. Help is on the way. You did the right thing
          by reaching out."
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
    },
    {
      key: makeid(10),
      name: 'callConclusion',
      type: 'radio',
      title: <p>Call Conclusion (Only When Appropriate)</p>,
      description: (
        <ScriptWrapper>
          If emergency services arrive or take over:
          <br />
          "I’m glad help is with you now. They’ll take it from here, but I’m
          really glad you stayed on the line."
          <br />
          If call disconnects unexpectedly: Escalate immediately per protocol.
        </ScriptWrapper>
      ),
      required: false,
      closable: true,
      options: [
        {
          key: makeid(10),
          label: 'Emergency services arrived',
          value: 'emergencyServicesArrived',
        },
        {
          key: makeid(10),
          label: 'Call transferred',
          value: 'callTransferred',
        },
        {
          key: makeid(10),
          label: 'Call disconnected prior to arrival',
          value: 'callDisconnectedPriorToArrival',
        },
      ],
    },
  ] as const satisfies TFormTemplateItem[];
};

type TAssist991TemplateValues = ExtractStepValues<
  ReturnType<typeof createAssist991Template>
>;

export const createAssist991Blurb = (
  data: TAssist991TemplateValues,
  ctx: BlurbContext,
) => {
  const {
    greetingComplianceHipaaVerification,
    emergencySituationReported,
    emergencySituationReportedOther,
    emergencyStatusConfirmed,
    patientLocationConfirmed,
    patientLocation,
    patientKeptOnLine,
    emergencyResponseInitiated,
    safetySupportProvided,
    callConclusion,
  } = data;

  const txt: string[] = [];

  if (
    greetingComplianceHipaaVerification?.length &&
    !hasField(ctx, 'greetingComplianceHipaaVerification')
  ) {
    addField(ctx, 'greetingComplianceHipaaVerification');

    const map = {
      patientIdentityVerified:
        'Greeting and compliance completed. Patient identity verified via name and date of birth.',
      recordedLineNotificationProvided:
        'Patient notified call may be recorded for quality and compliance purposes.',
      caregiverPresentConsentConfirmed:
        'Caregiver participation confirmed and patient consent obtained.',
    };
    txt.push(...mapValuesToText(greetingComplianceHipaaVerification, map));
  }

  if (
    emergencySituationReported?.length &&
    !hasField(ctx, 'emergencySituationReported')
  ) {
    addField(ctx, 'emergencySituationReported');

    const map = {
      acuteMedicalSymptomsReported: 'acute medical symptoms reported',
      suddenChangeInCondition: 'sudden change in condition',
      injuryOrAccident: 'injury or accident',
      other: emergencySituationReportedOther,
    };
    const items = mapValuesToText(emergencySituationReported, map);
    if (items.length) {
      txt.push(
        `Patient reported an emergency medical situation requiring immediate attention, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (emergencyStatusConfirmed && !hasField(ctx, 'emergencyStatusConfirmed')) {
    addField(ctx, 'emergencyStatusConfirmed');

    const map = {
      patientExperiencingSymptomsAtTimeOfCall:
        'patient experiencing symptoms at time of call',
      emergencyConcernOngoing: 'emergency concern ongoing',
    };
    txt.push(
      `Emergency status confirmed based on patient report: ${map[emergencyStatusConfirmed]}.`,
    );
  }

  if (patientLocationConfirmed && !hasField(ctx, 'patientLocationConfirmed')) {
    addField(ctx, 'patientLocationConfirmed');

    const locationMap = {
      locationConfirmed: 'confirmed',
      partialLocationObtained: 'partially obtained',
      unableToObtainLocation: 'unable to obtain',
    };

    let line = `Patient location ${locationMap[patientLocationConfirmed]} and documented.`;

    if (
      patientLocation &&
      patientLocation.trim() &&
      patientLocationConfirmed !== 'unableToObtainLocation'
    ) {
      line += ` Location reported as ${patientLocation.trim()}.`;
    }

    txt.push(line);
  }

  if (patientKeptOnLine && !hasField(ctx, 'patientKeptOnLine')) {
    addField(ctx, 'patientKeptOnLine');

    const map = {
      patientRemainedOnLine: 'patient remained on line',
      intermittentlyResponsive: 'intermittently responsive',
      callDisconnectedUnexpectedly: 'call disconnected unexpectedly',
    };
    const value = map[patientKeptOnLine];
    txt.push(
      `Patient engagement status during call: ${value}. Efforts made to keep patient engaged on the line throughout the interaction.`,
    );
  }

  if (
    emergencyResponseInitiated?.length &&
    !hasField(ctx, 'emergencyResponseInitiated')
  ) {
    addField(ctx, 'emergencyResponseInitiated');

    const map = {
      supervisorNotified: 'supervisor notified',
      teammateContacted911: 'teammate contacted 911',
      emergencyServicesDispatched: 'emergency services dispatched',
    };
    const items = mapValuesToText(emergencyResponseInitiated, map);
    if (items.length) {
      txt.push(
        `Emergency escalation initiated per protocol, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (
    safetySupportProvided?.length &&
    !hasField(ctx, 'safetySupportProvided')
  ) {
    addField(ctx, 'safetySupportProvided');

    const map = {
      encouragedSafePositioning: 'encouraged safe positioning',
      encouragedSupportPersonPresence: 'encouraged support person presence',
      monitoredSymptomChanges: 'monitored symptom changes',
      notAppropriateNotPossible: 'not appropriate or not possible',
    };
    const items = mapValuesToText(safetySupportProvided, map);
    if (items.length) {
      txt.push(
        `Safety support provided while awaiting emergency response, including ${formatList(
          items,
        )}.`,
      );
    }
  }

  if (callConclusion && !hasField(ctx, 'callConclusion')) {
    addField(ctx, 'callConclusion');

    const map = {
      emergencyServicesArrived: 'emergency services arrived',
      callTransferred: 'call transferred',
      callDisconnectedPriorToArrival: 'call disconnected prior to arrival',
    };
    txt.push(
      `Call concluded per escalation protocol (${map[callConclusion]}).`,
    );
  }

  return txt.join(' ');
};
